import { useEffect, useState, useRef } from 'react'
import { useGameContext } from '../GameContext'
import { useAuthFromContext } from '../AuthContext'
import { db } from '../FirebaseConfig'
import { ref, get, update } from 'firebase/database'
import { buildRoundEntry, archiveRound } from '../utils/warArchive'
import { ARMIES_PER_ROUND, STRUCTURES_TO_WIN, BATTLEFIELD_COUNT, BATTLEFIELD_CAPACITIES,
         ROCKET_COST, DRONE_COST, DRONE_FLIGHT_MS, DRONE_HOVER_MS,
         REPLAY_LEAD_MS, BASE_BUILD_WIGGLE_MS, BASE_BUILD_FLIGHT_MS,
         missileImpactMs, missileFlightTotalMs, MISSILE_SURPLUS_STAGGER_MS } from '../rules/gameConfig'
import { initSound, playSound } from '../utils/soundManager'
import {
    canAttack,
    moveUnit,
    undoMove,
    deployArmy,
    undoDeploy,
    buildFort,
    undoBuildFort,
    requiredDropZones,
    canPlaceDropZone,
    fittingDropZones,
    buildRoundActionsLedger,
    buildOppReplaySteps,
    matureCorridor,
} from '../rules/gameLogic'
// ── Homefront v3 (subplot block system) ──────────────────────────────────────
import { warOutcome } from '../rules/structureKills'
import { plotOf, isStructureDestroyed, battlefieldColumn, cloneHomefront, migrateBroods } from '../rules/subplotGrid'
import { canPlaceBase, addBrood, baseFootprint } from '../rules/subplotBases'
import { resolveRound } from '../rules/resolveRound'
import { makeRevealObserve } from '../rules/revealObserve'
import { submitOrders } from '../rules/submitOrders'
import { validateOrders } from '../rules/validateOrders'

// Hives saved before rules v8 keep their Broods among the structures.
const parseHf = (raw) => (raw ? migrateBroods(JSON.parse(raw)) : null)

export default function useGameState() {
    const { user } = useAuthFromContext()
    const { game, recordMatchResult } = useGameContext()

    // Preload battle SFX (creates a suspended audio context); handleReveal resumes it.
    useEffect(() => { initSound() }, [])

    const [selfLocalRoundData, setSelfLocalRoundData] = useState({ currentRound: 0, armiesToDeploy: 0 })
    const [oppArmiesToDeploy, setOppArmiesToDeploy] = useState(ARMIES_PER_ROUND)
    const [selfLocalScore, setSelfLocalScore] = useState(0)
    const [oppLocalScore, setOppLocalScore] = useState(0)
    const [selfLocalBoardArr, setSelfLocalBoardArr] = useState([])
    const [oppLocalBoardArr, setOppLocalBoardArr] = useState([])
    const [roundStartSelf, setRoundStartSelf] = useState()

    // Scores captured at submit time, before Firebase attack resolution can overwrite them.
    // Guarantees computeAttacks always gets pre-resolution values regardless of network timing.
    const capturedPreResScores = useRef(null)

    const [tempScore, setTempScore] = useState({ player1Score: 0, player2Score: 0 })
    const [selfTempBoardArr, setSelfTempBoardArr] = useState(null)
    const [oppTempBoardArr, setOppTempBoardArr] = useState(null)
    const [canSubmitAttackResult, setCanSubmitAttackResult] = useState(false)
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)

    const [animationEvents, setAnimationEvents] = useState([])
    const [animBoardSelf, setAnimBoardSelf] = useState(null)
    const [animBoardOpp, setAnimBoardOpp] = useState(null)
    // Opponent round replay: while pre-resolution holds (frost up through the
    // replay's end), the opp half renders this board instead of animBoardOpp —
    // first their previous-round board, then handleReveal steps it through
    // what they did this round before the clashes start.
    const [replayBoardOpp, setReplayBoardOpp] = useState(null)
    // { fromBoard, ledger } captured when resolution triggers; consumed by handleReveal.
    const replayDataRef = useRef(null)
    // This round's own actions ledger, captured at submit before maturation
    // erases the moved/green kinds; written as p{n}RoundActions.
    const roundActionsRef = useRef(null)
    // This round's orders ({ board, targets, drop zones, rockets, drones }),
    // captured at submit and handed to rules/submitOrders, which declares the
    // p{n}BaseBuilds that are merged onto the Grids at resolution.
    const submitOrdersRef = useRef(null)
    // The OPPONENT's fresh builds ({ round, builds }), withheld from their
    // displayed Grid until the replay's build beat flies the funding drones
    // down (see playOppReplay). baseBuildFx tells HexBoard to launch the
    // descent motes for one build beat.
    const oppFreshBuildsRef = useRef(null)
    const baseBuildFxKeyRef = useRef(0)
    const [baseBuildFx, setBaseBuildFx] = useState(null)
    const [showingPreResolution, setShowingPreResolution] = useState(false)
    // Ref mirrors showingPreResolution for synchronous reads inside the Firebase
    // listener closure, which captures a stale copy of the state value.
    const showingPreResolutionRef = useRef(false)
    const [isAnimating, setIsAnimating] = useState(false)
    // Ref mirrors isAnimating for synchronous reads inside the Firebase listener
    // closure. Distinct from showingPreResolutionRef — that becomes true when
    // pre-resolution data is ready (before REVEAL is clicked); this only becomes
    // true once the animation is actively playing.
    const isAnimatingRef = useRef(false)
    const [activeClashId, setActiveClashId] = useState(null)
    const [activeClashContext, setActiveClashContext] = useState(null)
    const [activeClashWinner, setActiveClashWinner] = useState(null)
    const [activeClashSelfStrength, setActiveClashSelfStrength] = useState(null)
    const [activeClashOppStrength, setActiveClashOppStrength] = useState(null)
    const [activeClashOppTargetIndex, setActiveClashOppTargetIndex] = useState(null)
    const [activeClashSelfTargetIndex, setActiveClashSelfTargetIndex] = useState(null)  // self-winner's struck plot row (chosen target or retaliation)
    const [activeClashAbsorbed, setActiveClashAbsorbed] = useState(0)  // margin soaked by the defender's rockets (interception beat)
    const [activeClashAimS, setActiveClashAimS] = useState(null)       // exact aim subplot of this clash's strike — missiles fly into it
    const [activeClashSelfHitInfo, setActiveClashSelfHitInfo] = useState(null)
    const [activeClashSelfHitsLog, setActiveClashSelfHitsLog] = useState(null)
    const [clashResult, setClashResult] = useState(null)
    const [animScore, setAnimScore] = useState(null)
    const [initialRenderDone, setInitialRenderDone] = useState(false)
    const postBattleSelfBoard = useRef(null)
    const animationAbortedRef = useRef(false)
    const pendingOppHfPlotsRef = useRef(null)
    const pendingSelfHfPlotsRef = useRef(null)
    const [message, setMessage] = useState('')
    const [warResult, setWarResult] = useState(null)
    const matchRecordedRef = useRef(false)

    // Homefront targeting. targetSubplots holds the exact aim subplot per column
    // (the source of truth for the crater); targetPlots mirrors its plot row for
    // the plot-level consumers (reveal anim, clash missile rows, submit checks).
    const [targetPlots, setTargetPlots] = useState(() => Array(BATTLEFIELD_COUNT).fill(null))
    const [targetSubplots, setTargetSubplots] = useState(() => Array(BATTLEFIELD_COUNT).fill(null))
    // Resolved battle events from the current round — used by submitPostAttackState
    const resolvedEventsRef = useRef(null)

    // Drop zones declared this round: one own-board column index per entry
    // (duplicates allowed when capacity permits). Required before submit — one
    // per mature Base — and hidden from the opponent until the resupply missile
    // is delivered next round. Placement mode (placingDropZones, derived below)
    // activates on its own once all missiles are deployed and placements are
    // still owed: deploy first, then drop zones, then submit.
    const [dropZones, setDropZones] = useState([])
    // Columns that received a resupply missile at the last resolution
    // ({ self, opp }) — consumed by the reveal to animate the arrivals.
    const [resupplyDeliveries, setResupplyDeliveries] = useState(null)

    // Yield balance for self (the global spendable production fund; persists per-account)
    const [yieldBalance, setYieldBalance] = useState(0)

    // Rockets bought this round but not yet submitted — an array of host subplot
    // flat indices (one entry per rocket). Flushed onto their structures + debited
    // from yield at submit; shown as "pending" (brighter) until then.
    const [pendingRockets, setPendingRockets] = useState([])

    // Surveillance drones bought this round: { launchS, targetS } per drone.
    // launchS = the live structure cell it lifts off from; targetS = the aim
    // subplot on the opponent's side of the SAME corridor (null until aimed —
    // submit requires every drone aimed). Written to p{n}DroneStrikes + debited
    // from yield at submit; the scan itself resolves with the round.
    const [pendingDrones, setPendingDrones] = useState([])

    // This round's resolved drone scans ({ self, opp } arrays of
    // { launchS, targetS, cells }), computed in computeAttacks and consumed by
    // handleReveal's drone phase. droneFlights is the slice HexBoard animates
    // while that phase plays. Opp scans carry launchS: null — the defender never
    // learns the launch cell, only the corridor.
    const [droneScans, setDroneScans] = useState(null)
    const [droneFlights, setDroneFlights] = useState(null)

    // Homefront v3: full subplot homefronts ({ subplots, structures }) for each side.
    const [selfHf, setSelfHf] = useState(null)
    const [oppHf,  setOppHf]  = useState(null)

    // Structure kills (each player's OWN count of structures lost). Lose
    // STRUCTURES_TO_WIN and the war is over. animLost holds the counts shown
    // during the reveal animation, ticked up as each dead structure's
    // silhouette lands on the kill bar.
    const [selfLost, setSelfLost] = useState(0)
    const [oppLost,  setOppLost]  = useState(0)
    const [animLost, setAnimLost] = useState(null)        // { self, opp } during reveal
    // Post-crater homefronts + kill counts computed at resolution, applied when the reveal ends.
    const pendingResolutionRef = useRef(null)

    // ── Match result recording ────────────────────────────────────────────────
    useEffect(() => {
        if (warResult && !matchRecordedRef.current) {
            matchRecordedRef.current = true
            // A war already closed (endedAt set — by the other client, or by
            // this one before a reload rebuilt the result) is not re-recorded.
            if (game?.endedAt == null) recordMatchResult(warResult)
        }
    }, [warResult]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Firebase sync: main game state ───────────────────────────────────────
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (user && game) {
            const selfUserId = user.uid
            const isPlayer1 = selfUserId === game.player1
            const selfCurrentRound = isPlayer1 ? game.p1CurrentRound : game.p2CurrentRound
            const oppCurrentRound  = isPlayer1 ? game.p2CurrentRound : game.p1CurrentRound
            const selfState        = isPlayer1 ? game.p1State        : game.p2State
            const oppState         = isPlayer1 ? game.p2State        : game.p1State
            const oppLastRoundState = isPlayer1 ? game.p2LastRound   : game.p1LastRound
            const selfScore        = isPlayer1 ? game.player1Score   : game.player2Score
            const oppScore         = isPlayer1 ? game.player2Score   : game.player1Score
            const selfGotAttackResults = isPlayer1 ? game.p1AttackResults : game.p2AttackResults
            const selfArmiesToDeploy   = isPlayer1 ? game.p1ArmiesToDeploy : game.p2ArmiesToDeploy
            const oppArmiesToDeployVal = isPlayer1 ? game.p2ArmiesToDeploy : game.p1ArmiesToDeploy
            const yieldVal             = isPlayer1 ? (game.p1Yield ?? 0) : (game.p2Yield ?? 0)
            // Homefront v3: full subplot homefronts + structures-lost counts.
            const selfHfRaw = isPlayer1 ? game.p1Homefront : game.p2Homefront
            const oppHfRaw  = isPlayer1 ? game.p2Homefront : game.p1Homefront
            const selfLostVal = isPlayer1 ? (game.p1StructuresLost ?? 0) : (game.p2StructuresLost ?? 0)
            const oppLostVal  = isPlayer1 ? (game.p2StructuresLost ?? 0) : (game.p1StructuresLost ?? 0)

            setSelfLocalScore(selfScore)
            setYieldBalance(yieldVal)
            setSelfLost(selfLostVal)
            setOppLost(oppLostVal)
            // While the reveal is playing, hold BOTH post-crater homefronts in refs and
            // apply them when the animation finishes (so craters — and the subplots they
            // reveal — don't pop in before the reveal plays). Otherwise reflect Firebase
            // immediately. Self is gated identically to opp so both sides reveal in sync.
            // Opp EXCEPTION: when the opp is a round ahead (they submitted, we haven't),
            // their submit has already flushed this round's rockets onto their homefront
            // — hold our previous snapshot so those placements stay hidden until both
            // sides have submitted (mirrors the oppLastRoundState board gating below).
            const oppAheadOfSelf = (oppCurrentRound ?? 0) > (selfCurrentRound ?? 0)
            if (showingPreResolutionRef.current) {
                pendingSelfHfPlotsRef.current = parseHf(selfHfRaw)
                if (!oppAheadOfSelf) pendingOppHfPlotsRef.current = parseHf(oppHfRaw)
            } else {
                setSelfHf(parseHf(selfHfRaw))
                setOppHf(prev => (oppAheadOfSelf && prev) ? prev : parseHf(oppHfRaw))
            }
            setOppLocalScore(oppScore)
            setOppArmiesToDeploy(oppArmiesToDeployVal ?? ARMIES_PER_ROUND)
            setRoundStartSelf(selfState)
            setOppLocalBoardArr(oppCurrentRound <= selfCurrentRound ? oppState : oppLastRoundState)
            setIsSubmitDisabled(selfCurrentRound > oppCurrentRound)

            if (oppCurrentRound > selfCurrentRound && isAnimatingRef.current) {
                animationAbortedRef.current = true
                showingPreResolutionRef.current = false
                setShowingPreResolution(false)
                setAnimBoardSelf(null)
                setAnimBoardOpp(null)
                setAnimScore(null)
                isAnimatingRef.current = false
                setIsAnimating(false)
                setAnimLost(null)
                setDroneFlights(null)
                setDroneScans(null)
                setReplayBoardOpp(null)
                replayDataRef.current = null
                setAnimationEvents([])
                // The round it was revealing still happened: its kills, its
                // war result, and the board this player now plans on (the
                // post-battle one — without it they'd plan on the pre-battle
                // board, which the rules refuse at submit).
                const cut = pendingResolutionRef.current
                if (cut) { setSelfLost(cut.selfLostAfter); setOppLost(cut.oppLostAfter) }
                if (cut?.warResult) setWarResult(cut.warResult)
                if (postBattleSelfBoard.current) {
                    setSelfLocalBoardArr(postBattleSelfBoard.current)
                    postBattleSelfBoard.current = null
                }
                setTargetPlots(Array(BATTLEFIELD_COUNT).fill(null))
                setTargetSubplots(Array(BATTLEFIELD_COUNT).fill(null))
                setDropZones([])
                pendingResolutionRef.current = null
                if (pendingSelfHfPlotsRef.current !== null) {
                    setSelfHf(pendingSelfHfPlotsRef.current)
                    pendingSelfHfPlotsRef.current = null
                }
                if (pendingOppHfPlotsRef.current !== null) {
                    setOppHf(pendingOppHfPlotsRef.current)
                    pendingOppHfPlotsRef.current = null
                }
            }

            // The war's end, rebuilt from the persisted kill counts whenever
            // this client has no reveal of its own pending: a reload, a round
            // adopted from the opponent's persist, a reveal cut short.
            if (!showingPreResolutionRef.current && !isAnimatingRef.current) {
                const ended = warOutcome(selfLostVal, oppLostVal)
                if (ended.over) setWarResult(prev => prev ?? (ended.result === 'WIN' ? 'win' : ended.result === 'LOSS' ? 'loss' : 'draw'))
            }

            if ((oppCurrentRound === selfCurrentRound) && selfCurrentRound > 1 && (!selfGotAttackResults) && !showingPreResolutionRef.current
                && game.attackSubmittedForRound === selfCurrentRound) {
                // This round's resolution was already computed AND persisted —
                // by the opponent's client, or by a previous session of this
                // one (this client missed the live equal-rounds window: a
                // throttled tab, a reconnect, a reload). The p{n}State /
                // homefronts synced above are already post-battle; recomputing
                // battles from them would re-resolve an already-resolved round
                // (and eat fresh Bases whose declarations were consumed), so
                // adopt the persisted result, settle the results flag, and
                // skip this round's reveal animation.
                console.warn('Adopting persisted round resolution — missed the live reveal window.')
                setSelfLocalRoundData(prevData => ({
                    ...prevData,
                    currentRound: selfCurrentRound,
                    armiesToDeploy: selfArmiesToDeploy,
                }))
                // Plan on the persisted post-battle board, as a played reveal
                // would leave it. The local board still holds this client's
                // own pre-battle submit (matured in place): drones that died
                // in the battle stand on it, and SUBMIT is refused because
                // the plan no longer accounts for the drones the rules hold.
                setSelfLocalBoardArr(selfState)
                setTargetPlots(Array(BATTLEFIELD_COUNT).fill(null))
                setTargetSubplots(Array(BATTLEFIELD_COUNT).fill(null))
                setDropZones([])
                setRevealedAtRound(selfCurrentRound)
                logPlayerGotAttackResults(isPlayer1)
            } else if ((oppCurrentRound === selfCurrentRound) && selfCurrentRound > 1 && (!selfGotAttackResults) && !showingPreResolutionRef.current) {
                setSelfLocalRoundData(prevData => ({
                    ...prevData,
                    currentRound: selfCurrentRound,
                    armiesToDeploy: selfArmiesToDeploy,
                }))
                setSelfLocalBoardArr(prev => [...prev])
                const scores = capturedPreResScores.current ?? { self: selfScore, opp: oppScore }
                capturedPreResScores.current = null
                // Opp round replay: capture their previous board (as this player
                // last saw it) and their submitted actions ledger, and hold the
                // opp half at that previous board through the frost — the new
                // positions must not pop in under it. handleReveal steps the
                // replay from here before the first clash.
                const oppActionsRaw = isPlayer1 ? game.p2RoundActions : game.p1RoundActions
                let oppLedger = null
                try { oppLedger = oppActionsRaw ? JSON.parse(oppActionsRaw) : null } catch { oppLedger = null }
                replayDataRef.current = { fromBoard: oppLastRoundState ?? null, ledger: oppLedger }
                setReplayBoardOpp(oppLastRoundState ?? null)
                computeAttacks(isPlayer1, selfState, oppState, scores.self, scores.opp)
            }

            if (selfGotAttackResults && (oppCurrentRound === selfCurrentRound)) {
                setSelfLocalRoundData(prevData => ({
                    ...prevData,
                    currentRound: selfCurrentRound,
                    armiesToDeploy: selfArmiesToDeploy,
                }))
                // A settled round with nothing pending on this client — a
                // reload after the reveal played, or a round adopted from the
                // opponent's persist — is revealed by definition. Marking it
                // here keeps the SUBMITTED/READY overlay from re-arming a
                // REVEAL that has nothing behind it (handleReveal refuses
                // those, so the overlay would otherwise never drop).
                if (!showingPreResolutionRef.current && !isAnimatingRef.current && resolvedEventsRef.current === null) {
                    setRevealedAtRound(selfCurrentRound)
                }
            }
        }
    }, [user, game])

    // ── Firebase sync: initial render ────────────────────────────────────────
    useEffect(() => {
        if (!initialRenderDone && user && game && selfLocalRoundData) {
            const selfUserId = user.uid
            const isPlayer1  = selfUserId === game.player1
            const selfState  = isPlayer1 ? game.p1State        : game.p2State
            const selfCurrentRound   = isPlayer1 ? game.p1CurrentRound : game.p2CurrentRound
            const selfArmiesToDeploy = isPlayer1 ? game.p1ArmiesToDeploy : game.p2ArmiesToDeploy
            setSelfLocalBoardArr(selfState)
            setSelfLocalRoundData(prevData => ({
                ...prevData,
                currentRound: selfCurrentRound,
                armiesToDeploy: selfArmiesToDeploy,
            }))
            setInitialRenderDone(true)
        }
    }, [user, game, initialRenderDone, selfLocalRoundData])

    // ── Firebase sync: round start board reset ───────────────────────────────
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (user && game) {
            const selfUserId = user.uid
            const isPlayer1  = selfUserId === game.player1
            const selfState  = isPlayer1 ? game.p1State : game.p2State
            if (JSON.stringify(roundStartSelf) !== JSON.stringify(selfState)) {
                setSelfLocalBoardArr(selfState)
            }
        }
    }, [roundStartSelf])

    // ── Firebase sync: submit post-attack state ───────────────────────────────
    // Persists the round's resolution (post-battle boards, post-crater
    // homefronts, kill counts) and consumes the round's declaration keys.
    // EITHER client can land this write — both derive byte-identical results
    // (battles and craters are deterministic), so whichever computes first
    // persists and the other sees the attackSubmittedForRound stamp and skips.
    // This is what makes the resolution survive one client missing the live
    // reveal window (throttled tab, reconnect, reload): P1-only persistence
    // was a single point of failure that silently dropped whole rounds —
    // fresh Bases included — when P1's write never ran.
    // No runTransaction: its first pass runs against the local cache, which
    // can be null and made the callback throw, killing the persist. get()
    // then a guarded update() instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (canSubmitAttackResult) {
            async function submitPostAttackState() {
                // Capture before any await — the reveal's ending clears these.
                const res = resolvedEventsRef.current
                const selfBoards = selfTempBoardArr
                const oppBoards = oppTempBoardArr
                const score = tempScore
                const finish = () => {
                    setCanSubmitAttackResult(false)
                    setOppTempBoardArr(null)
                    setSelfTempBoardArr(null)
                }
                // Nothing locally resolved (the adoption path: this client
                // skipped the compute because the round was already persisted).
                if (!res || res.round == null || !selfBoards || !oppBoards) { finish(); return }

                const selfKey = res.isSelfPlayer1 ? 'p1' : 'p2'
                const oppKey  = res.isSelfPlayer1 ? 'p2' : 'p1'
                // No fort bonus: Bases pay out in drop-zone resupply missiles
                // (already injected into the post-battle boards), not extra deploys.
                const updates = {}
                updates[`${selfKey}State`] = JSON.stringify(selfBoards)
                updates[`${oppKey}State`]  = JSON.stringify(oppBoards)
                updates.player1Score = score.player1Score
                updates.player2Score = score.player2Score
                updates.p1ArmiesToDeploy = ARMIES_PER_ROUND
                updates.p2ArmiesToDeploy = ARMIES_PER_ROUND
                // Homefront v3: post-crater subplot homefronts + kill counts.
                if (res.selfHfAfter) updates[`${selfKey}Homefront`] = JSON.stringify(res.selfHfAfter)
                if (res.oppHfAfter)  updates[`${oppKey}Homefront`]  = JSON.stringify(res.oppHfAfter)
                updates[`${selfKey}StructuresLost`] = res.selfLostAfter ?? 0
                updates[`${oppKey}StructuresLost`]  = res.oppLostAfter ?? 0
                // Yield is credited at resolution, off the post-round Grids.
                updates[`${selfKey}Yield`] = res.selfYieldAfter ?? 0
                updates[`${oppKey}Yield`]  = res.oppYieldAfter ?? 0
                // The declared Base builds are now baked into the persisted
                // homefronts — consume the keys in the SAME write, so they can
                // neither be re-merged from stale data nor outlive a reveal
                // they were never stamped into.
                updates.p1BaseBuilds = JSON.stringify([])
                updates.p2BaseBuilds = JSON.stringify([])
                updates.attackSubmittedForRound = res.round

                const gameRef = ref(db, `games/${game.id}`)
                for (let attempt = 0; attempt < 3; attempt++) {
                    try {
                        const snapshot = await get(gameRef)
                        if (!snapshot.exists()) break
                        const cur = snapshot.val()
                        // Someone already persisted this round's resolution.
                        if (cur.attackSubmittedForRound === res.round) break
                        // The reveal window has closed (a player has submitted
                        // the next round) — writing now would clobber their
                        // fresh submission with old results.
                        if (cur.p1CurrentRound !== res.round || cur.p2CurrentRound !== res.round) {
                            console.warn('Post-attack persist skipped: reveal window closed for round', res.round)
                            break
                        }
                        await update(gameRef, updates)
                        // The war archive's copy of this round — its own
                        // best-effort write AFTER the live persist landed, so
                        // it can never stand in the game's way. `cur` holds
                        // both players' declarations and the pre-round state.
                        await archiveRound(cur, buildRoundEntry({ cur, res, updates }))
                        break
                    } catch (e) {
                        if (attempt === 2) console.error('Post-attack persist failed after retries', e)
                        else await new Promise(r => setTimeout(r, 800))
                    }
                }
                finish()
            }
            submitPostAttackState()
        }
    }, [canSubmitAttackResult])

    // ── Attack resolution ─────────────────────────────────────────────────────

    async function logPlayerGotAttackResults(isSelfPlayer1) {
        // Single-key update, no runTransaction: the transaction's first pass
        // ran against the local cache, which can be null on a fresh session
        // and made the callback throw — silently dropping this write AND the
        // persist it gates. A targeted update can't clobber the opponent's
        // flag, so no transaction is needed.
        const gameRef = ref(db, `games/${game.id}`)
        const key = isSelfPlayer1 ? 'p1AttackResults' : 'p2AttackResults'
        try {
            await update(gameRef, { [key]: true })
            console.log(`${(isSelfPlayer1 ? 'Player 1' : 'Player 2')} logged getting Attack results`)
        } catch (e) {
            // Not fatal: the persist below is independently guarded, and the
            // resolution-stamp check on the next sync re-runs this settle.
            console.error('Failed to log attack results', e)
        }
        setCanSubmitAttackResult(true)
    }

    function computeAttacks(isSelfPlayer1, selfGameState, oppGameState, selfGameScore, oppGameScore) {
        if (!selfGameState || !oppGameState) {
            console.error('p1 or p2 State not getting saved in db')
            return
        }
        // The round itself is rules/resolveRound: the clashes and their
        // craters resolve INTERLEAVED, corridor by corridor (1 → 7, the order
        // the reveal plays them), from this client's seat. Both clients read
        // the same snapshot and walk the same order, so the results stay
        // byte-identical. What stays here is the reveal: the per-clash
        // display snapshots, taken by the observer at the moment each
        // corridor's crater lands.
        const player = (k, board) => ({
            board,
            homefront: game?.[`${k}Homefront`] ?? null,
            targetPlots: game?.[`${k}TargetPlots`] ?? null,
            targetSubplots: game?.[`${k}TargetSubplots`] ?? null,
            dropZones: game?.[`${k}DropZones`] ?? null,
            baseBuilds: game?.[`${k}BaseBuilds`] ?? null,
            droneStrikes: game?.[`${k}DroneStrikes`] ?? null,
            structuresLost: game?.[`${k}StructuresLost`] ?? 0,
            yield: game?.[`${k}Yield`] ?? 0,
        })
        const selfKey = isSelfPlayer1 ? 'p1' : 'p2'
        const oppKey  = isSelfPlayer1 ? 'p2' : 'p1'
        // The crater seed's round: the counter after both submits bumped it.
        const round = game?.p1CurrentRound ?? 0

        // Per clash: the cumulative post-clash snapshot (craters up to and
        // including this column) handleReveal swaps in as the clash's
        // missiles land, the crater's intermediate stages, and the ledger
        // each corridor fought with (rules/revealObserve).
        const observe = makeRevealObserve()

        const result = resolveRound(
            { p1: player('p1', isSelfPlayer1 ? selfGameState : oppGameState),
              p2: player('p2', isSelfPlayer1 ? oppGameState : selfGameState),
              round },
            { perspective: selfKey, observe },
        )
        const enrichedEvents = result.events
        const selfOut = result[selfKey], oppOut = result[oppKey]
        const selfDetail = result.detail[selfKey], oppDetail = result.detail[oppKey]
        if (selfDetail.failedBuilds.length || oppDetail.failedBuilds.length) {
            console.error('Base build(s) failed to stamp at resolution — refunding drones:',
                          { self: selfDetail.failedBuilds, opp: oppDetail.failedBuilds })
        }
        const selfHomefront = selfOut.homefront
        const oppHomefront  = oppOut.homefront
        const selfLostAfter = selfOut.structuresLost
        const oppLostAfter  = oppOut.structuresLost
        const selfBoardFinal = selfOut.board
        const oppBoardFinal  = oppOut.board

        setResupplyDeliveries({
            self: selfDetail.delivery.delivered, opp: oppDetail.delivery.delivered,
            sources: { self: selfDetail.delivery.sources, opp: oppDetail.delivery.sources },
        })
        // This round's drone scans, per side. launchS is withheld from the
        // defender's view — they only ever see the corridor.
        const selfScanEvents = selfDetail.scans
        const oppScanEvents  = oppDetail.scans.map(s => ({ ...s, launchS: null }))
        setDroneScans(selfScanEvents.length || oppScanEvents.length
            ? { self: selfScanEvents, opp: oppScanEvents }
            : null)

        // The war's end, decided with the round (applied when the reveal
        // ends, or straight away if the reveal is cut short).
        const warResultAfter = !result.outcome.over ? null
            : result.outcome.draw ? 'draw'
            : result.outcome.winner === selfKey ? 'win' : 'loss'

        // Post-crater homefronts + kill counts + yields, persisted by
        // whichever client lands submitPostAttackState first.
        resolvedEventsRef.current = {
            events: enrichedEvents, isSelfPlayer1, round,
            selfHfAfter: selfHomefront, oppHfAfter: oppHomefront,
            selfLostAfter, oppLostAfter,
            selfYieldAfter: selfOut.yield, oppYieldAfter: oppOut.yield,
            // For the war archive's round record (utils/warArchive): where
            // resupply landed and what the drones scanned, per side.
            resupply: { self: selfDetail.delivery.delivered, opp: oppDetail.delivery.delivered },
            scans: { self: selfScanEvents.map(e => e.targetS), opp: oppScanEvents.map(e => e.targetS) },
        }
        pendingResolutionRef.current = {
            selfHomefront, oppHomefront,
            selfLostAfter, oppLostAfter,
            warResult: warResultAfter,
        }

        setAnimationEvents(enrichedEvents)
        setAnimBoardSelf(selfDetail.boardAtOpen)
        setAnimBoardOpp(oppDetail.boardAtOpen)
        setAnimScore({ self: selfGameScore, opp: oppGameScore })
        // Kill bars animate from the pre-round counts, greying one icon per
        // dead structure as its silhouette lands during the reveal.
        const preSelfLost = isSelfPlayer1 ? (game?.p1StructuresLost ?? 0) : (game?.p2StructuresLost ?? 0)
        const preOppLost  = isSelfPlayer1 ? (game?.p2StructuresLost ?? 0) : (game?.p1StructuresLost ?? 0)
        setAnimLost({ self: preSelfLost, opp: preOppLost })
        showingPreResolutionRef.current = true
        setShowingPreResolution(true)
        // Show the PRE-crater homefronts for the whole reveal — identically for both
        // sides. `game.*Homefront` is still pre-crater here (the post-crater write only
        // happens after logPlayerGotAttackResults, below), so newly-damaged land stays
        // hidden until handleReveal swaps in the craters at the animation's end. Driving
        // this from local state (not the Firebase sync's else-branch) makes the reveal
        // deterministic — it can't be pre-empted by the post-crater write landing early.
        // Clear any stale pending homefronts so the end-of-reveal swap uses the freshly
        // computed post-crater result (pendingResolutionRef) unless Firebase re-pushes.
        pendingSelfHfPlotsRef.current = null
        pendingOppHfPlotsRef.current = null
        // Fresh Base builds are visible from this reveal. Your OWN build merges
        // into the pre-crater display immediately (you watched yourself place
        // it); the OPPONENT's is withheld — their replay's build beat flies the
        // funding drones down and the construction appears as they land
        // (playOppReplay merges it; any build the replay can't show merges
        // right after the replay, before the clashes).
        const parseBuilds = (raw) => (raw ? JSON.parse(raw) : [])
        const selfBaseArr = parseBuilds(game?.[`${selfKey}BaseBuilds`])
        const oppBaseArr  = parseBuilds(game?.[`${oppKey}BaseBuilds`])
        const displaySelfHf = parseHf(game?.[`${selfKey}Homefront`])
        const displayOppHf  = parseHf(game?.[`${oppKey}Homefront`])
        if (displaySelfHf) {
            for (const b of selfBaseArr) {
                if (b?.centerS == null || b.col == null) continue
                addBrood(displaySelfHf, b.col, b.centerS, round)
            }
        }
        oppFreshBuildsRef.current = {
            round,
            builds: (oppBaseArr ?? []).filter(b => b?.col != null && b?.centerS != null),
        }
        setSelfHf(displaySelfHf)
        setOppHf(displayOppHf)
        // Scores are no longer the win condition (structure kills are); keep them steady.
        setTempScore({ player1Score: game?.player1Score ?? 0, player2Score: game?.player2Score ?? 0 })
        setSelfTempBoardArr(selfBoardFinal)
        setOppTempBoardArr(oppBoardFinal)
        postBattleSelfBoard.current = selfBoardFinal
        logPlayerGotAttackResults(isSelfPlayer1)
    }

    async function handleReveal() {
        // Only a round THIS client has resolved can be revealed. A REVEAL
        // that lands while the round is still unresolved (the compute not yet
        // run) must leave the overlay up and play nothing:
        // dropping it would expose the previous round's board — the deploys
        // already spent, SUBMIT live — and replaying last round's events
        // would wipe the local round state (targets, drop zones) for
        // a round that was never resolved. Returns whether the reveal ran.
        const resolvedForRound = resolvedEventsRef.current
        if (!resolvedForRound || resolvedForRound.round !== fbSelfRound || !animationEvents || animationEvents.length === 0) {
            console.warn('REVEAL ignored — round', fbSelfRound, 'is not resolved on this client yet.')
            return false
        }
        // Uses the Firebase round (what revealOverlayUp compares against) —
        // the local round can lag when the opponent has raced ahead a round.
        setRevealedAtRound(fbSelfRound)
        initSound()  // REVEAL click is the user gesture that unlocks/resumes audio
        animationAbortedRef.current = false
        isAnimatingRef.current = true
        setIsAnimating(true)
        const delay = ms => new Promise(res => setTimeout(res, ms))
        // ── Opponent round replay ── before the clashes: the opp half steps
        // through what they did this round — each cross-corridor move hops as
        // its own beat, a new Base rises, then fresh deploys glide in from
        // their rear edge — so the player reads the changed defense before
        // missiles fly. (Self needs no replay: you know what you did.)
        await playOppReplay(delay)
        // Any fresh opponent build the replay couldn't show lands now, before
        // the clashes need it on the field.
        mergeRemainingOppBases()
        if (animationAbortedRef.current) return
        for (let eventCol = 0; eventCol < animationEvents.length; eventCol++) {
            const event = animationEvents[eventCol]
            if (animationAbortedRef.current) return
            if (event.type === 'NO_BATTLE') continue
            const { battlefieldId, type } = event
            // The corridor fights with the ledger it had when ITS turn came —
            // an earlier corridor's crater may have cratered (or killed) a
            // Base here — so the volley MissileLayer fires matches the
            // strength the engine litigated with.
            const applyLedger = (prev, ledger) =>
                (prev && ledger) ? prev.map(bf => (bf.id === battlefieldId ? { ...bf, ...ledger } : bf)) : prev
            setAnimBoardSelf(prev => applyLedger(prev, event.selfLedgerAtClash))
            setAnimBoardOpp(prev => applyLedger(prev, event.oppLedgerAtClash))
            setActiveClashId(battlefieldId)
            setActiveClashContext(event.context)
            setActiveClashWinner(type === 'WIN' ? 'self' : type === 'LOSS' ? 'opp' : 'tie')
            setActiveClashSelfStrength(event.selfStrength)
            setActiveClashOppStrength(event.oppStrength)
            // Surplus missiles fly toward the struck plot row (chosen target, or the
            // seeded retaliation row when the winner won on defense).
            setActiveClashOppTargetIndex(
                type === 'LOSS' ? (event.oppTargetIndex ?? event.retaliationRow ?? null) : null
            )
            setActiveClashSelfTargetIndex(
                type === 'WIN' ? ((targetPlots[eventCol] ?? null) !== null ? targetPlots[eventCol] : (event.retaliationRow ?? null)) : null
            )
            setActiveClashAbsorbed(event.absorbed ?? 0)
            setActiveClashAimS(event.aimS ?? null)
            setActiveClashSelfHitInfo(null)
            setActiveClashSelfHitsLog(null)
            setClashResult(null)
            const struckStructure = (event.destroyedStructures?.length ?? 0) > 0
            // Whether the winner's leftover shots fly as aimed surplus shots or stay
            // on the slower dueling cadence — must mirror MissileLayer's targetIdx
            // inputs, which include the seeded retaliation row on BOTH sides (a
            // defender's win aims its surplus at the retaliation row too).
            const winnerHasTarget =
                (type === 'WIN'  && ((targetPlots[eventCol] ?? null) !== null || (event.retaliationRow ?? null) !== null)) ||
                (type === 'LOSS' && ((event.oppTargetIndex ?? null) !== null || (event.retaliationRow ?? null) !== null))
            if (type !== 'TIE') {
                const pairCount = Math.min(event.selfStrength ?? 0, event.oppStrength ?? 0)
                const missileArrivalMs = winnerHasTarget
                    ? missileImpactMs(pairCount, event.absorbed ?? 0)
                    : 900
                // Surface this clash's subplot damage AS the strikes land — STAGED:
                // each landing missile buys the next ring of the footprint ladder
                // (9 → 17 → 25 → 29 subplots), so the crater grows one swap per
                // impact on the surplus cadence instead of appearing whole with the
                // first shot. `craterStages` holds the intermediate cumulative
                // snapshots; the authoritative post-clash snapshot lands with the
                // last footprint-growing shot (shots past the 4th add nothing).
                // Washed clashes have no stages — their single swap just consumes
                // the fired rockets, whose lift-off has already animated by then.
                if (event.postClashOppHf || event.postClashSelfHf) {
                    const swapAt = (hf, ms) => setTimeout(() => {
                        if (animationAbortedRef.current) return
                        if (event.postClashOppHf)  setOppHf(hf)
                        if (event.postClashSelfHf) setSelfHf(hf)
                    }, ms)
                    const stages = event.craterStages ?? []
                    stages.forEach((hf, i) => swapAt(hf, missileArrivalMs + i * MISSILE_SURPLUS_STAGGER_MS))
                    swapAt(event.postClashOppHf ?? event.postClashSelfHf,
                           missileArrivalMs + stages.length * MISSILE_SURPLUS_STAGGER_MS)
                }
                // The kill bar is driven by the dead structures' silhouettes: as
                // the fire sweep finishes a structure's footprint, HexBoard lifts
                // its silhouette off the map and flies it to the struck side's next
                // intact icon; the landing ticks the animated count (markAnimKill)
                // and the icon greys. The per-clash landings sum to exactly
                // event.kills, and handleReveal's ending snaps the committed counts
                // regardless, so a missed silhouette can't corrupt state. Empty
                // land and partial damage fly nothing — cells aren't score.
            }
            // Hold the clash open long enough for the LAST missile to fly in and strike,
            // not just a fixed window. With many missiles (or after rockets intercept the
            // leading shots), the trailing missiles arrive late; a fixed delay unmounts
            // them mid-flight. missileFlightTotalMs() mirrors HexBoard's flight timing.
            // +450ms padding so the LAST unit tile's fade-out (which starts as its
            // missile launches and runs ~0.85s, just past the missile's own impact)
            // finishes before the result resolves — otherwise the trailing tile snaps
            // away instead of fading like the first one did.
            const lastMissileMs = missileFlightTotalMs(event.selfStrength, event.oppStrength, winnerHasTarget) + 450
            await delay(Math.max(struckStructure ? 3000 : 1800, lastMissileMs))
            if (animationAbortedRef.current) return
            // Washed columns resolve as a tie — the interception already played out.
            setClashResult(event.washedByRockets ? 'TIE' : type)
            await delay(1680)
            if (animationAbortedRef.current) return
            setAnimBoardSelf(prev => {
                if (!prev) return prev
                return prev.map(bf => {
                    if (bf.id !== battlefieldId) return bf
                    return { ...bf, armyQty: 0, fortCount: 0, fortInConstruction: 0, newArmies: 0, deliveredNewArmies: 0, movedInArmies: 0, isAttacking: false }
                })
            })
            setAnimBoardOpp(prev => {
                if (!prev) return prev
                return prev.map(bf => {
                    if (bf.id !== battlefieldId) return bf
                    return { ...bf, armyQty: 0, fortCount: 0, fortInConstruction: 0 }
                })
            })
            setActiveClashId(null)
            setActiveClashContext(null)
            setActiveClashWinner(null)
            setActiveClashSelfStrength(null)
            setActiveClashOppStrength(null)
            setActiveClashOppTargetIndex(null)
            setActiveClashSelfTargetIndex(null)
            setActiveClashAbsorbed(0)
            setActiveClashAimS(null)
            setActiveClashSelfHitInfo(null)
            setActiveClashSelfHitsLog(null)
            setClashResult(null)
        }
        if (animationAbortedRef.current) return

        // ── Drone surveillance phase ── after the clashes: each side's drones
        // fly (yours from its launch structure, the opponent's in from the
        // divider down its corridor), then the post-scan homefronts swap in at
        // touchdown — HexBoard's scan diff sweeps the fresh cells in from each
        // target. The drones hover through the sweep, then lift away.
        if (droneScans && (droneScans.self.length > 0 || droneScans.opp.length > 0)) {
            setDroneFlights(droneScans)
            // One buzz at lift-off, tapered out as the drones fade — several
            // simultaneous drones share the voice (slightly boosted) rather
            // than stacking into a swarm.
            const droneCount = droneScans.self.length + droneScans.opp.length
            playSound('drone', {
                gainScale: Math.min(1 + 0.15 * (droneCount - 1), 1.3),
                duration: (DRONE_FLIGHT_MS + DRONE_HOVER_MS) / 1000,
            })
            await delay(DRONE_FLIGHT_MS)
            if (animationAbortedRef.current) return
            const resolvedMid = pendingResolutionRef.current
            if (resolvedMid) {
                setSelfHf(resolvedMid.selfHomefront)
                setOppHf(resolvedMid.oppHomefront)
            }
            await delay(DRONE_HOVER_MS)
            if (animationAbortedRef.current) return
            setDroneFlights(null)
        }
        setDroneScans(null)

        isAnimatingRef.current = false
        setIsAnimating(false)
        showingPreResolutionRef.current = false
        setShowingPreResolution(false)

        // Apply the post-crater homefronts + final kill counts now that the reveal has played.
        const resolved = pendingResolutionRef.current
        if (resolved) {
            setSelfLost(resolved.selfLostAfter)
            setOppLost(resolved.oppLostAfter)
        }
        if (pendingSelfHfPlotsRef.current !== null) {
            setSelfHf(pendingSelfHfPlotsRef.current)
            pendingSelfHfPlotsRef.current = null
        } else if (resolved) {
            setSelfHf(resolved.selfHomefront)
        }
        if (pendingOppHfPlotsRef.current !== null) {
            setOppHf(pendingOppHfPlotsRef.current)
            pendingOppHfPlotsRef.current = null
        } else if (resolved) {
            setOppHf(resolved.oppHomefront)
        }
        setAnimLost(null)

        // ── End-of-War ────────────────────────────────────────────────────────
        // Decided with the round (resolveRound's outcome); shown once its
        // reveal has played.
        if (resolved?.warResult) setWarResult(resolved.warResult)

        if (postBattleSelfBoard.current) {
            setSelfLocalBoardArr(postBattleSelfBoard.current)
            postBattleSelfBoard.current = null
        }
        setTargetPlots(Array(BATTLEFIELD_COUNT).fill(null))
        setTargetSubplots(Array(BATTLEFIELD_COUNT).fill(null))
        setDropZones([])
        resolvedEventsRef.current = null
        pendingResolutionRef.current = null
        // Spent: a later REVEAL must never replay this round's clashes.
        setAnimationEvents([])
        setAnimBoardSelf(null)
        setAnimBoardOpp(null)
        setAnimScore(null)
        return true
    }

    // Steps the opponent's half through their round (movements, builds, then
    // fresh deploys), landing exactly on their submitted board. Bails to no
    // replay — the pre-existing instant swap — whenever the diff can't be
    // fully explained (old client without a ledger, uid rebuild, missing
    // previous board).
    async function playOppReplay(delay) {
        const data = replayDataRef.current
        replayDataRef.current = null
        try {
            const steps = data?.fromBoard && animBoardOpp
                ? buildOppReplaySteps(data.fromBoard, animBoardOpp, data.ledger)
                : null
            if (!steps) return
            await delay(REPLAY_LEAD_MS)
            for (const step of steps) {
                if (animationAbortedRef.current) return
                const builds = step.buildCol != null
                    ? (oppFreshBuildsRef.current?.builds ?? []).filter(b => b.col === step.buildCol)
                    : []
                if (builds.length) {
                    // Fire the descent motes while the funding tiles are still
                    // mounted (this step's board removes them), give HexBoard
                    // a frame to clone them, then let the tiles vanish and the
                    // constructions appear as the motes land. Several builds
                    // in this corridor descend together — buildPairs[i] is the
                    // funding pair for builds[i] (both captured in build order).
                    setBaseBuildFx({
                        col: step.buildCol,
                        builds: builds.map((b, i) => ({
                            centerS: b.centerS,
                            uids: step.buildPairs?.[i] ?? [],
                        })),
                        key: ++baseBuildFxKeyRef.current,
                    })
                    await delay(50)
                    setReplayBoardOpp(step.board)
                    // Merge the constructions a beat BEFORE the motes land so
                    // React has painted the bars (fading up, see the
                    // --base-con entrance) as the drones touch down — landing
                    // on bare dirt with the bars popping late is the seam this
                    // hides. The send-off started ~50ms ago (at the fx
                    // effect): wiggle first, then the descent.
                    const preLand = Math.max(0, Math.min(step.holdMs, BASE_BUILD_WIGGLE_MS + BASE_BUILD_FLIGHT_MS - 130) - 50)
                    await delay(preLand)
                    mergeOppFreshBase(step.buildCol)
                    await delay(Math.max(0, step.holdMs - preLand - 50))
                } else {
                    setReplayBoardOpp(step.board)
                    await delay(step.holdMs)
                }
            }
        } finally {
            setReplayBoardOpp(null)
            setBaseBuildFx(null)
        }
    }

    // Merge one corridor's withheld opponent builds onto their displayed Grid
    // (their replay motes just landed) — or, after the replay, whatever the
    // replay couldn't show (no ledger, aborted steps, no build beat).
    function mergeOppFreshBase(col) {
        const data = oppFreshBuildsRef.current
        const builds = (data?.builds ?? []).filter(x => x.col === col)
        if (!builds.length) return
        data.builds = data.builds.filter(x => x.col !== col)
        setOppHf(prev => {
            if (!prev) return prev
            const hf = cloneHomefront(prev)
            for (const b of builds) addBrood(hf, b.col, b.centerS, data.round)
            return hf
        })
    }
    function mergeRemainingOppBases() {
        const data = oppFreshBuildsRef.current
        if (!data?.builds?.length) return
        const builds = data.builds
        data.builds = []
        setOppHf(prev => {
            if (!prev) return prev
            const hf = cloneHomefront(prev)
            for (const b of builds) addBrood(hf, b.col, b.centerS, data.round)
            return hf
        })
    }

    // One dead structure's silhouette landed on a kill bar during the reveal:
    // tick that side's animated count by one (its next icon greys). No-ops
    // once the reveal has ended (animLost cleared) so a straggler can't touch
    // the committed counts.
    function markAnimKill(side) {
        setAnimLost(prev => prev ? { ...prev, [side]: (prev[side] ?? 0) + 1 } : prev)
    }

    // ── Action dispatcher ─────────────────────────────────────────────────────

    function onAction(bfId, action, extras) {
        if (warResult !== null) return
        switch (action) {
            case 'attack':
                setSelfLocalBoardArr(prev => prev.map(bf => {
                    if (bf.id !== bfId) return bf
                    if (bf.isAttacking) return bf
                    const { valid, reason } = canAttack(bf)
                    if (!valid) { setMessage(reason); return bf }
                    return { ...bf, isAttacking: true }
                }))
                {
                    // An attacking corridor's broods fire and owe no drop
                    // zone: drop any already placed beyond what is now owed.
                    const col = selfLocalBoardArr.findIndex(bf => bf.id === bfId)
                    const bf = selfLocalBoardArr[col]
                    if (bf && !bf.isAttacking && canAttack(bf).valid) {
                        const next = selfLocalBoardArr.map((b, i) => (i === col ? { ...b, isAttacking: true } : b))
                        const owed = Math.min(requiredDropZones(next), openSlotCount)
                        setDropZones(prev => (prev.length > owed ? prev.slice(0, owed) : prev))
                    }
                }
                break

            case 'undoAttack': {
                const undoCol = selfLocalBoardArr.findIndex(bf => bf.id === bfId)
                setSelfLocalBoardArr(prev => prev.map(bf =>
                    bf.id !== bfId ? bf : { ...bf, isAttacking: false }
                ))
                if (undoCol !== -1) {
                    setTargetPlots(prev => {
                        const next = [...prev]
                        next[undoCol] = null
                        return next
                    })
                    setTargetSubplots(prev => {
                        const next = [...prev]
                        next[undoCol] = null
                        return next
                    })
                }
                break
            }

            case 'move': {
                const { success, reason, boardArr } = moveUnit(selfLocalBoardArr, bfId, extras?.direction, extras?.uid ?? null)
                if (!success) { setMessage(reason); break }
                setSelfLocalBoardArr(boardArr)
                break
            }

            case 'undoMove':
                setSelfLocalBoardArr(prev => {
                    const { success, reason, boardArr } = undoMove(prev, bfId, extras?.uid ?? null)
                    if (!success) { setMessage(reason); return prev }
                    return boardArr
                })
                break

            case 'buildFort':
                // extras.centerS = the Base's Grid position (bar-footprint
                // center), picked in HexBoard's placement overlay; extras.uids
                // = the two veterans the player paired to fund it. The Grid
                // side is validated here against the homefront PLUS this
                // round's still-pending builds (not yet on the
                // Grid); gameLogic owns the corridor side. No per-corridor
                // cap — real estate is the only limit.
                setSelfLocalBoardArr(prev => {
                    const col = prev.findIndex(bf => bf.id === bfId)
                    const centerS = extras?.centerS ?? null
                    const pendingCells = col === -1 ? new Set() : new Set(
                        (prev[col].pendingBaseBuilds ?? []).flatMap(p => baseFootprint(p.centerS) ?? [])
                    )
                    const footprint = baseFootprint(centerS) ?? []
                    if (col === -1 || !canPlaceBase(selfHf, col, centerS) ||
                        footprint.some(s => pendingCells.has(s))) {
                        setMessage('Pick a clear spot in this corridor for your brood.')
                        return prev
                    }
                    const { success, reason, boardArr } = buildFort(prev, bfId, centerS, extras?.uids ?? null)
                    if (!success) { setMessage(reason); return prev }
                    return boardArr
                })
                break

            case 'undoBuildFort':
                // extras.centerS targets one specific pending build (its ghost
                // was tapped); omitted, the most recent build comes back.
                setSelfLocalBoardArr(prev => {
                    const { success, reason, boardArr } = undoBuildFort(prev, bfId, extras?.centerS ?? null)
                    if (!success) { setMessage(reason); return prev }
                    return boardArr
                })
                break

            case 'deploy': {
                // Placement never prompts for a plan: occupying an empty corridor
                // auto-sets the default (deployArmy computes it from the neighbors).
                const result = deployArmy(selfLocalBoardArr, bfId, selfLocalRoundData.armiesToDeploy)
                if (!result.success) { setMessage(result.reason); return }
                setSelfLocalBoardArr(result.boardArr)
                setSelfLocalRoundData(prev => ({ ...prev, armiesToDeploy: result.armiesToDeploy }))
                break
            }

            case 'remove': {
                const result = undoDeploy(selfLocalBoardArr, bfId, selfLocalRoundData.armiesToDeploy)
                if (!result.success) { setMessage(result.reason); break }
                setSelfLocalBoardArr(result.boardArr)
                setSelfLocalRoundData(prev => ({ ...prev, armiesToDeploy: result.armiesToDeploy }))
                break
            }

            default:
                break
        }
    }

    // Rocket/drone buy-undo on a self-side structure subplot. Pending until
    // submit: purchases are held in pendingRockets / pendingDrones (funded from
    // yield) and flushed at submit. One asset per subplot — a cell hosts a
    // rocket OR launches a drone, never both, so each keeps its own glyph and
    // undo tap target.
    const pendingSpend = pendingRockets.length * ROCKET_COST + pendingDrones.length * DRONE_COST

    function onHomefrontAction(subS, action) {
        if (warResult !== null) return
        const cell = selfHf?.subplots?.[subS]
        const structureId = cell?.structureId
        if (structureId == null || cell.destroyed) return
        if (isStructureDestroyed(selfHf, structureId)) return

        const hasDeployedRocket = (selfHf.structures[structureId]?.rocketCells || []).includes(subS)
        const hasPendingDrone   = pendingDrones.some(d => d.launchS === subS)

        if (action === 'buyRocket') {
            if (hasDeployedRocket || pendingRockets.includes(subS)) {
                setMessage('That subplot already has a rocket.')
                return
            }
            if (hasPendingDrone) {
                setMessage('That subplot is launching a recon plane this round.')
                return
            }
            const spendable = yieldBalance - pendingSpend
            if (spendable < ROCKET_COST) {
                setMessage(`Need ${ROCKET_COST} yield to buy a rocket (have ${spendable}).`)
                return
            }
            setPendingRockets(prev => [...prev, subS])
        } else if (action === 'undoRocket') {
            setPendingRockets(prev => {
                const i = prev.lastIndexOf(subS)
                if (i === -1) return prev
                const next = [...prev]
                next.splice(i, 1)
                return next
            })
        } else if (action === 'buyDrone') {
            if (hasDeployedRocket || pendingRockets.includes(subS)) {
                setMessage('That subplot already hosts a rocket.')
                return
            }
            if (hasPendingDrone) {
                setMessage('That subplot is already launching a recon plane.')
                return
            }
            const spendable = yieldBalance - pendingSpend
            if (spendable < DRONE_COST) {
                setMessage(`Need ${DRONE_COST} yield to buy a recon plane (have ${spendable}).`)
                return
            }
            setPendingDrones(prev => [...prev, { launchS: subS, targetS: null }])
        } else if (action === 'undoDrone') {
            setPendingDrones(prev => {
                const i = prev.map(d => d.launchS).lastIndexOf(subS)
                if (i === -1) return prev
                const next = [...prev]
                next.splice(i, 1)
                return next
            })
        }
    }

    // Aim a pending drone: the tapped subplot must sit on the opponent's side
    // of the drone's own corridor (HexBoard only offers the picker there, but
    // the corridor check is the rule's backstop). Fills the first unaimed drone
    // in that corridor; with all of them aimed, re-aims the last one.
    function onDroneTarget(col, subS) {
        if (warResult !== null) return
        // Aim is locked at submit (same rule as onTargetPlot — the declared
        // strikes are already in Firebase).
        if (revealOverlayUp) return
        if (battlefieldColumn(subS) !== col) return
        setPendingDrones(prev => {
            const inCol = (d) => battlefieldColumn(d.launchS) === col
            let idx = prev.findIndex(d => inCol(d) && d.targetS == null)
            if (idx === -1) {
                for (let i = prev.length - 1; i >= 0; i--) {
                    if (inCol(prev[i])) { idx = i; break }
                }
            }
            if (idx === -1) return prev
            const next = [...prev]
            next[idx] = { ...next[idx], targetS: subS }
            return next
        })
    }

    // Drop zones owed this round, capped by the board's open slots so a fully
    // packed board can't soft-lock submit (nowhere to place → never able to
    // submit). Bases consume no capacity — airborne drones only.
    const openSlotCount = selfLocalBoardArr.reduce((sum, bf) =>
        sum + Math.max(0, bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))), 0)
    const requiredDropZoneCount = Math.min(requiredDropZones(selfLocalBoardArr), openSlotCount)

    // A drone moved (or deployed) into a slot a placed drop zone was holding:
    // that zone goes back to be placed again, and the player is told so.
    useEffect(() => {
        if (!dropZones.length) return
        const kept = fittingDropZones(selfLocalBoardArr, dropZones)
        if (kept === dropZones) return
        setDropZones(kept)
        const n = dropZones.length - kept.length
        setMessage(`A drone took ${n === 1 ? 'a drop zone\'s slot' : `${n} drop zones' slots`}. Place ${n === 1 ? 'it' : 'them'} again.`)
    }, [selfLocalBoardArr]) // eslint-disable-line react-hooks/exhaustive-deps

    // Mirrors NextRoundModal's visibility: the SUBMITTED/OPPONENT READY overlay
    // is up from submit until REVEAL is clicked this round. Radial menus must
    // stay down while it shows.
    const [revealedAtRound, setRevealedAtRound] = useState(null)
    // Overlay window = from self's submit until REVEAL is clicked. Computed from
    // the raw Firebase rounds (exactly like NextRoundModal's visibility), NOT
    // from selfLocalRoundData: the local round only advances once BOTH players
    // have submitted, so it can't see the waiting-for-opponent window and the
    // self-side frost would miss it.
    const fbIsPlayer1 = user && game && user.uid === game.player1
    const fbSelfRound = user && game ? (fbIsPlayer1 ? game.p1CurrentRound : game.p2CurrentRound) : 0
    const fbOppRound  = user && game ? (fbIsPlayer1 ? game.p2CurrentRound : game.p1CurrentRound) : 0
    const revealOverlayUp = fbSelfRound > 1 && revealedAtRound !== fbSelfRound
    // True once the opponent has also submitted (overlay in its READY state):
    // the whole board — both halves — frosts beneath the modals until reveal.
    const oppReadyOverlayUp = revealOverlayUp && fbOppRound >= fbSelfRound

    // Placement mode: deploys are spent, drop zones still owed, round still open.
    // Derived, so it enters the moment the last missile lands and exits the moment
    // the last bullseye is placed (or a deploy is undone, or the round submits).
    const placingDropZones = warResult === null && !isSubmitDisabled && !showingPreResolution &&
        selfLocalRoundData.armiesToDeploy === 0 &&
        requiredDropZoneCount > 0 && dropZones.length < requiredDropZoneCount

    // Place/remove a drop-zone marker on an own-board column. Placement is
    // capacity-gated (pending markers on the column count as occupants).
    function onDropZone(col, action) {
        if (warResult !== null) return
        if (action === 'place') {
            if (dropZones.length >= requiredDropZoneCount) {
                setMessage('All drop zones placed.')
                return
            }
            const { valid, reason } = canPlaceDropZone(selfLocalBoardArr, col, dropZones)
            if (!valid) { setMessage(reason); return }
            setDropZones(prev => [...prev, col])
        } else if (action === 'remove') {
            setDropZones(prev => {
                const i = prev.lastIndexOf(col)
                if (i === -1) return prev
                const next = [...prev]
                next.splice(i, 1)
                return next
            })
        }
    }

    // The player taps an exact subplot on the opp field; the mirrored plot row is
    // derived for the animation/consumer paths that still think in plots.
    function onTargetPlot(col, subS) {
        if (warResult !== null) return
        // Aim is locked at submit: the target was already written to Firebase
        // and is what resolution will use on both clients — a post-submit
        // re-aim would only desync this client's display from the real crater.
        if (revealOverlayUp) return
        setTargetSubplots(prev => {
            const next = [...prev]
            next[col] = subS
            return next
        })
        setTargetPlots(prev => {
            const next = [...prev]
            next[col] = plotOf(subS).row
            return next
        })
    }

    // ── Submit ────────────────────────────────────────────────────────────────

    const [canSubmit, setCanSubmit] = useState(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (canSubmit) {
            async function submitGameState() {
                const gameRef = ref(db, `games/${game.id}`)
                const snapshot = await get(gameRef)
                if (snapshot.exists()) {
                    const currentGameData = snapshot.val()
                    const selfPlayerId = user.uid
                    const updates = {}
                    const isPlayer1 = currentGameData.player1 === selfPlayerId
                    const k = isPlayer1 ? 'p1' : (currentGameData.player2 === selfPlayerId ? 'p2' : null)
                    const orders = submitOrdersRef.current
                    if (k && orders) {
                        // The round's orders go through rules/submitOrders:
                        // the corridors mature, bought rockets are stationed
                        // on the Grid as it stands in Firebase now, and the
                        // rocket + plane spend comes off yield. (No income
                        // here — yield is credited at resolution.)
                        let submitted
                        try {
                            submitted = submitOrders(
                                {
                                    board: JSON.parse(currentGameData[`${k}State`]),
                                    homefront: currentGameData[`${k}Homefront`] ?? null,
                                    yield: currentGameData[`${k}Yield`] ?? 0,
                                },
                                orders,
                            )
                        } catch (e) {
                            // handleSubmit checked the same plan; this only trips
                            // when Firebase moved under it. Nothing is written.
                            console.error('Submit refused:', e)
                            setMessage(e.errors?.[0] ?? 'Those orders could not be submitted.')
                            submitOrdersRef.current = null
                            setCanSubmit(false)
                            return
                        }
                        updates[`${k}LastRound`] = currentGameData[`${k}State`]
                        updates[`${k}State`] = JSON.stringify(submitted.board)
                        updates[`${k}CurrentRound`] = currentGameData[`${k}CurrentRound`] + 1
                        updates[`${k}AttackResults`] = false
                        updates[`${k}ArmiesToDeploy`] = ARMIES_PER_ROUND
                        updates[`${k}TargetPlots`] = JSON.stringify(submitted.targetPlots)
                        updates[`${k}TargetSubplots`] = JSON.stringify(submitted.targetSubplots)
                        updates[`${k}DropZones`] = JSON.stringify(submitted.dropZones)
                        // Declared always, so a drone-less / build-less round
                        // clears last round's.
                        updates[`${k}DroneStrikes`] = JSON.stringify(submitted.droneStrikes)
                        updates[`${k}BaseBuilds`] = JSON.stringify(submitted.baseBuilds)
                        // This round's actions ledger (captured pre-maturation in
                        // handleSubmit) — the opponent's client replays it at reveal.
                        updates[`${k}RoundActions`] = JSON.stringify(roundActionsRef.current ?? [])
                        if (submitted.rocketsStationed.length) updates[`${k}Homefront`] = JSON.stringify(submitted.homefront)
                        // The rockets bought this round, written every submit: the
                        // Grid above now shows them, and a bot opponent deciding
                        // after this submit must not (bot/botOpponent.js).
                        updates[`${k}RocketsBought`] = JSON.stringify(submitted.rocketsStationed)
                        if (submitted.spent > 0) updates[`${k}Yield`] = submitted.yield
                        console.log(`SUBMITTED as Player ${k === 'p1' ? 1 : 2}!`)
                    }
                    roundActionsRef.current = null
                    submitOrdersRef.current = null

                    await update(gameRef, updates)
                    setPendingRockets([])
                    setPendingDrones([])
                }
                setCanSubmit(false)
            }
            submitGameState()
        }
    }, [canSubmit])

    function handleSubmit() {
        if (warResult !== null) return
        if (selfLocalRoundData.armiesToDeploy) {
            setMessage(`Cannot submit yet. You must deploy ${selfLocalRoundData.armiesToDeploy} ${selfLocalRoundData.armiesToDeploy === 1 ? 'army' : 'armies'} before submitting.`)
            return
        }
        const allAttackTargetsDefined = selfLocalBoardArr.every((bf, i) => !bf.isAttacking || (targetPlots?.[i] ?? null) !== null)
        if (!allAttackTargetsDefined) {
            setMessage('Define a target on all corridors where you are attacking.')
            return
        }
        // Every mature Base owes a drop zone before the round can be finalized.
        if (dropZones.length < requiredDropZoneCount) {
            const n = requiredDropZoneCount
            setMessage(`You have ${n} brood${n === 1 ? '' : 's'} with an egg — place ${n} drop zone${n === 1 ? '' : 's'} before submitting.`)
            return
        }
        // Every drone bought this round must be aimed before launch.
        if (pendingDrones.some(d => d.targetS == null)) {
            setMessage('Aim your recon plane before submitting — pick a subplot in its corridor.')
            return
        }
        // Capture scores now — before Firebase attack resolution can overwrite them.
        // The Firebase listener may fire with post-resolution scores by the time
        // computeAttacks runs (race with the opponent's submitPostAttackState write).
        capturedPreResScores.current = { self: selfLocalScore, opp: oppLocalScore }
        // Capture this round's actions for the opponent's replay BEFORE
        // maturation erases the moved/green kinds it distinguishes by.
        roundActionsRef.current = buildRoundActionsLedger(selfLocalBoardArr)
        // The round's orders, as planned (pre-maturation: submitOrders reads
        // the declared brood builds off the pending records before maturing).
        const orders = {
            board: selfLocalBoardArr,
            targetPlots, targetSubplots, dropZones,
            rockets: pendingRockets,
            drones: pendingDrones,
        }
        // The rules' final say (rules/validateOrders): everything the board and
        // the checks above gate, plus what only they know (spend, targets in
        // their corridor, units accounted for).
        const v = validateOrders({ board: roundStartSelf ?? selfLocalBoardArr, homefront: selfHf, yield: yieldBalance }, orders)
        if (!v.ok) {
            setMessage(v.errors[0])
            return
        }
        submitOrdersRef.current = orders
        // Round-end merge, shown at once: greens/moved-in mature into veterans
        // IN PLACE (slots keep every unit's plot), round flags reset.
        setSelfLocalBoardArr(selfLocalBoardArr.map(matureCorridor))
        setCanSubmit(true)
    }

    // ── Derived values ────────────────────────────────────────────────────────

    const selfBoard = showingPreResolution && animBoardSelf ? animBoardSelf : selfLocalBoardArr
    // Opp board during pre-resolution: the replay board when one is holding
    // (their previous round through the frost, then the replay's steps),
    // otherwise the pre-battle board the clash animates over.
    const oppBoard  = showingPreResolution && (replayBoardOpp || animBoardOpp)
        ? (replayBoardOpp ?? animBoardOpp)
        : oppLocalBoardArr
    const isPaired  = !!(game?.player1 && game?.player2)
    const warCapacities = game?.warCapacities ?? BATTLEFIELD_CAPACITIES

    // Kill bars: animate during the reveal, otherwise reflect committed state.
    const selfLostDisplay = showingPreResolution && animLost ? animLost.self : selfLost
    const oppLostDisplay  = showingPreResolution && animLost ? animLost.opp  : oppLost

    return {
        // Board state (animation-aware)
        selfBoard,
        oppBoard,

        // Scores
        selfScore: showingPreResolution && animScore ? animScore.self : selfLocalScore,
        oppScore:  showingPreResolution && animScore ? animScore.opp  : oppLocalScore,

        // Round info
        currentRound:      selfLocalRoundData.currentRound,
        armiesToDeploy:    selfLocalRoundData.armiesToDeploy,
        oppArmiesToDeploy: oppArmiesToDeploy,
        canDeploy:         selfLocalRoundData.armiesToDeploy > 0,

        // Pairing
        isPaired,

        // UI state
        isSubmitDisabled,
        message,
        setMessage,

        // Actions
        onAction,
        handleSubmit,
        handleReveal,

        // Animation
        isAnimating,
        showingPreResolution,
        activeClashId,
        activeClashContext,
        activeClashWinner,
        activeClashSelfStrength,
        activeClashOppStrength,
        activeClashOppTargetIndex,
        activeClashSelfTargetIndex,
        activeClashAbsorbed,
        activeClashAimS,
        activeClashSelfHitInfo,
        activeClashSelfHitsLog,
        clashResult,

        // War result
        warResult,

        // Targeting
        targetPlots,
        targetSubplots,
        onTargetPlot,

        // Drop zones / resupply
        dropZones,
        requiredDropZoneCount,
        onDropZone,
        placingDropZones,
        resupplyDeliveries,

        // SUBMITTED / OPPONENT READY overlay visibility — UI suppresses radial
        // menus beneath it.
        revealOverlayUp,
        oppReadyOverlayUp,

        // Yield economy — display yield nets out this round's pending rocket + drone spend.
        yieldBalance: yieldBalance - pendingSpend,
        onHomefrontAction,
        pendingRockets,
        canAffordRocket: (yieldBalance - pendingSpend) >= ROCKET_COST,

        // Surveillance drones
        pendingDrones,
        canAffordDrone: (yieldBalance - pendingSpend) >= DRONE_COST,
        onDroneTarget,
        droneFlights,

        // Opponent replay: one build beat's funding-drone descent (HexBoard
        // launches the motes off this).
        baseBuildFx,

        // Homefront v3: subplot homefronts + the structure-kill scoreboard.
        selfHf,
        oppHf,
        selfLost: selfLostDisplay,
        oppLost:  oppLostDisplay,
        onStructureKilled: markAnimKill,
        structuresToWin: STRUCTURES_TO_WIN,

        // War capacities for grid layout
        warCapacities,

        // Exposed for direct board manipulation if needed externally
        selfLocalBoardArr,
        setSelfLocalBoardArr,
        selfLocalRoundData,
        setSelfLocalRoundData,
    }
}
